/* eslint-disable max-lines */
import { memo, useMemo, useState } from "react"
import type { ReactNode } from "react"

import { saveAs } from "file-saver"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { useTranslation } from "react-i18next"
import { utils, write } from "xlsx"

import type { CostByUserReport, CostByUserRow, CostUsageTrendPoint } from "../types"

import { Activity, AlertTriangle, ArrowUpRight, Coins, Download, Users } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { EmptyState } from "@/components/ui/empty-state"
import { Skeleton } from "@/components/ui/skeleton"
import { SortableTableHeader } from "@/components/ui/sortable-table-header"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { CollapsibleDashboardCard } from "./collapsible-dashboard-card"
import { CostUsageTrendChart } from "./cost-usage-trend-chart"
import { MobileSortControls } from "./mobile-sort-controls"

const INITIAL_VISIBLE_COUNT = 15

type SortState = {
  key: "user" | "cost" | "share" | "tokens" | "requests" | "activity" | "full" | null
  direction: "asc" | "desc" | null
}

function getDisplayName(user: CostByUserRow) {
  return user.profile.email || user.userId
}

function formatActivityTimestamp(value?: string | null) {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleString("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatActivityDate(value?: string | null) {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
}

function formatCompactNumber(value: number) {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return value.toString()
}

export const CostsByUserTable = memo(function CostsByUserTable({
  data,
  isLoading,
  trendData,
  isTrendLoading,
  trendCurrency,
  open,
  onOpenChange,
  sharedPerUserMonthly,
}: {
  data: CostByUserReport | null
  isLoading: boolean
  trendData?: CostUsageTrendPoint[] | null
  isTrendLoading?: boolean
  trendCurrency?: "SEK" | "USD" | "EUR"
  /** When provided, the card renders as a controlled collapsible. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Monthly shared amount added to each user. Omit to keep the table unchanged. */
  sharedPerUserMonthly?: number
}) {
  const { t } = useTranslation()
  const [sort, setSort] = useState<SortState>({ key: "cost", direction: "desc" })
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT)

  const currency = data?.currency ?? trendCurrency ?? "USD"
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat("sv-SE", {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }),
    [currency]
  )

  const users = data?.users ?? []
  const summary = useMemo(() => {
    if (data?.summary) return data.summary
    if (users.length === 0) {
      return {
        totalUsers: 0,
        totalCost: 0,
        totalTokens: 0,
        totalRequests: 0,
        totalErrors: 0,
      }
    }
    return users.reduce(
      (acc, user) => ({
        totalUsers: acc.totalUsers + 1,
        totalCost: acc.totalCost + user.usage.cost,
        totalTokens: acc.totalTokens + user.usage.tokensTotal,
        totalRequests: acc.totalRequests + user.usage.requests,
        totalErrors: acc.totalErrors + user.usage.errors,
      }),
      {
        totalUsers: 0,
        totalCost: 0,
        totalTokens: 0,
        totalRequests: 0,
        totalErrors: 0,
      }
    )
  }, [data?.summary, users])

  const totalCost = summary.totalCost

  const maxCost = useMemo(() => {
    if (users.length === 0) return 0
    return Math.max(...users.map((user) => user.usage.cost))
  }, [users])

  const showFullCost = typeof sharedPerUserMonthly === "number"
  const fullMonthlyCost = (periodCost: number) => {
    const days = data?.rangeDays && data.rangeDays > 0 ? data.rangeDays : 30
    return periodCost * (30 / days) + (sharedPerUserMonthly ?? 0)
  }

  const sortedUsers = useMemo(() => {
    if (!sort.key || !sort.direction) return users
    return [...users].sort((a, b) => {
      let aValue: string | number
      let bValue: string | number

      switch (sort.key) {
        case "user":
          aValue = getDisplayName(a).toLowerCase()
          bValue = getDisplayName(b).toLowerCase()
          break
        case "share":
          aValue = totalCost > 0 ? a.usage.cost / totalCost : 0
          bValue = totalCost > 0 ? b.usage.cost / totalCost : 0
          break
        case "tokens":
          aValue = a.usage.tokensTotal
          bValue = b.usage.tokensTotal
          break
        case "requests":
          aValue = a.usage.requests
          bValue = b.usage.requests
          break
        case "activity":
          aValue = a.usage.lastActivity ? new Date(a.usage.lastActivity).getTime() : 0
          bValue = b.usage.lastActivity ? new Date(b.usage.lastActivity).getTime() : 0
          break
        case "full":
          aValue = fullMonthlyCost(a.usage.cost)
          bValue = fullMonthlyCost(b.usage.cost)
          break
        case "cost":
        default:
          aValue = a.usage.cost
          bValue = b.usage.cost
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      return sort.direction === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number)
    })
  }, [users, sort, totalCost, data?.rangeDays, sharedPerUserMonthly])

  const visibleUsers = useMemo(
    () => sortedUsers.slice(0, visibleCount),
    [sortedUsers, visibleCount]
  )

  const hasMore = sortedUsers.length > visibleCount
  const isExpanded = visibleCount > INITIAL_VISIBLE_COUNT && visibleCount >= sortedUsers.length

  const handleSort = (key: SortState["key"]) => {
    if (!key) return
    setSort((prev) => {
      if (prev.key === key) {
        if (prev.direction === "asc") return { key, direction: "desc" }
        if (prev.direction === "desc") return { key: null, direction: null }
        return { key, direction: "asc" }
      }
      return { key, direction: "asc" }
    })
  }

  const normalizeCompany = (company?: string | null) =>
    company?.trim() || t("adminDashboard.costsByUser.export.unknownCompany")

  const exportRows = useMemo(
    () =>
      sortedUsers.map((user) => {
        const row: Record<string, string | number> = {
          [t("adminDashboard.costsByUser.export.user")]: getDisplayName(user),
          [t("adminDashboard.costsByUser.export.company")]: normalizeCompany(user.profile.company),
          [t("adminDashboard.costsByUser.export.department")]: user.profile.department ?? "",
          [t("adminDashboard.costsByUser.export.jobTitle")]: user.profile.jobTitle ?? "",
          [t("adminDashboard.costsByUser.export.requests")]: user.usage.requests,
          [t("adminDashboard.costsByUser.export.tokensIn")]: user.usage.tokensIn,
          [t("adminDashboard.costsByUser.export.tokensOut")]: user.usage.tokensOut,
          [t("adminDashboard.costsByUser.export.tokensTotal")]: user.usage.tokensTotal,
          [t("adminDashboard.costsByUser.export.cost", { currency })]: user.usage.cost,
        }
        if (typeof sharedPerUserMonthly === "number") {
          row[t("localAiLeadPage.fullCost.shared", "Shared / month")] = sharedPerUserMonthly
          row[t("localAiLeadPage.fullCost.full", "Full cost / month")] = fullMonthlyCost(
            user.usage.cost
          )
        }
        row[t("adminDashboard.costsByUser.export.lastActivity")] = formatActivityTimestamp(
          user.usage.lastActivity
        )
        return row
      }),
    [sortedUsers, t, currency, sharedPerUserMonthly, data?.rangeDays]
  )

  const summaryCards = data
    ? [
        {
          id: "totalCost",
          icon: Coins,
          label: t("adminDashboard.costsByUser.kpis.totalCost"),
          value: formatter.format(summary.totalCost),
        },
        {
          id: "totalTokens",
          icon: Activity,
          label: t("adminDashboard.costsByUser.kpis.totalTokens"),
          value: summary.totalTokens.toLocaleString("sv-SE"),
        },
        {
          id: "totalRequests",
          icon: ArrowUpRight,
          label: t("adminDashboard.costsByUser.kpis.totalRequests"),
          value: summary.totalRequests.toLocaleString("sv-SE"),
        },
        {
          id: "totalErrors",
          icon: AlertTriangle,
          label: t("adminDashboard.costsByUser.kpis.totalErrors"),
          value: summary.totalErrors.toLocaleString("sv-SE"),
        },
      ]
    : []

  const handleExportExcel = () => {
    if (!data) return
    const worksheet = utils.json_to_sheet(exportRows)
    const companySummaryMap = new Map<
      string,
      {
        company: string
        cost: number
        share: number
        requests: number
        tokensTotal: number
        users: number
      }
    >()

    sortedUsers.forEach((user) => {
      const company = normalizeCompany(user.profile.company)
      const existing = companySummaryMap.get(company)

      if (existing) {
        existing.cost += user.usage.cost
        existing.requests += user.usage.requests
        existing.tokensTotal += user.usage.tokensTotal
        existing.users += 1
      } else {
        companySummaryMap.set(company, {
          company,
          cost: user.usage.cost,
          share: 0,
          requests: user.usage.requests,
          tokensTotal: user.usage.tokensTotal,
          users: 1,
        })
      }
    })

    const companySummaryRows = Array.from(companySummaryMap.values())

    companySummaryRows.forEach((row) => {
      row.share = totalCost > 0 ? (row.cost / totalCost) * 100 : 0
    })

    companySummaryRows.sort((a, b) => b.cost - a.cost)

    const companyWorksheet = utils.json_to_sheet(
      companySummaryRows.map((row) => ({
        [t("adminDashboard.costsByUser.export.company")]: row.company,
        [t("adminDashboard.costsByUser.export.userCount")]: row.users,
        [t("adminDashboard.costsByUser.export.requests")]: row.requests,
        [t("adminDashboard.costsByUser.export.tokensTotal")]: row.tokensTotal,
        [t("adminDashboard.costsByUser.export.cost", { currency })]: row.cost,
        [t("adminDashboard.costsByUser.export.costShare")]: row.share,
      }))
    )

    const workbook = utils.book_new()
    utils.book_append_sheet(workbook, worksheet, "CostsByUser")
    utils.book_append_sheet(workbook, companyWorksheet, "CostByCompany")
    const buffer = write(workbook, { bookType: "xlsx", type: "array" })
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
    saveAs(blob, `hello-ai-costs-by-user-${data.rangeDays}d-${currency}.xlsx`)
  }

  const handleExportPdf = () => {
    if (!data) return
    const doc = new jsPDF({ orientation: "landscape" })
    doc.setFontSize(14)
    doc.text(
      t("adminDashboard.costsByUser.export.title", {
        days: data.rangeDays,
        currency,
      }),
      14,
      16
    )
    const fullCostHead =
      typeof sharedPerUserMonthly === "number"
        ? [
            t("localAiLeadPage.fullCost.shared", "Shared / month"),
            t("localAiLeadPage.fullCost.full", "Full cost / month"),
          ]
        : []
    autoTable(doc, {
      startY: 22,
      head: [
        [
          t("adminDashboard.costsByUser.export.user"),
          t("adminDashboard.costsByUser.export.company"),
          t("adminDashboard.costsByUser.export.department"),
          t("adminDashboard.costsByUser.export.requests"),
          t("adminDashboard.costsByUser.export.tokensTotal"),
          t("adminDashboard.costsByUser.export.cost", { currency }),
          ...fullCostHead,
          t("adminDashboard.costsByUser.export.lastActivity"),
        ],
      ],
      body: sortedUsers.map((user) => [
        getDisplayName(user),
        normalizeCompany(user.profile.company),
        user.profile.department ?? "",
        user.usage.requests.toLocaleString("sv-SE"),
        user.usage.tokensTotal.toLocaleString("sv-SE"),
        formatter.format(user.usage.cost),
        ...(typeof sharedPerUserMonthly === "number"
          ? [
              formatter.format(sharedPerUserMonthly),
              formatter.format(fullMonthlyCost(user.usage.cost)),
            ]
          : []),
        formatActivityTimestamp(user.usage.lastActivity),
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [30, 64, 175] },
    })
    doc.save(`hello-ai-costs-by-user-${data.rangeDays}d-${currency}.pdf`)
  }

  const exportActions = (
    <>
      <Button
        disabled={!data || isLoading}
        size="sm"
        type="button"
        variant="outline"
        onClick={handleExportExcel}
      >
        <Download className="mr-2 size-4" />
        {t("adminDashboard.costsByUser.export.excel")}
      </Button>
      <Button
        disabled={!data || isLoading}
        size="sm"
        type="button"
        variant="outline"
        onClick={handleExportPdf}
      >
        <Download className="mr-2 size-4" />
        {t("adminDashboard.costsByUser.export.pdf")}
      </Button>
    </>
  )

  // Render the body inside either a plain Card (default) or a controlled
  // collapsible card, so the page can gate the costs-by-user query on `open`.
  const wrap = (body: ReactNode, withExport: boolean) =>
    open !== undefined && onOpenChange ? (
      <CollapsibleDashboardCard
        headerActions={withExport ? exportActions : undefined}
        icon={Coins}
        id="costs-card"
        open={open}
        title={t("adminDashboard.costsByUser.title")}
        onOpenChange={onOpenChange}
      >
        {body}
      </CollapsibleDashboardCard>
    ) : (
      <Card className="rounded-lg shadow-sm">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Coins className="size-5" />
              {t("adminDashboard.costsByUser.title")}
            </CardTitle>
          </div>
          {withExport && <div className="flex items-center gap-2">{exportActions}</div>}
        </CardHeader>
        <CardContent className="space-y-6">{body}</CardContent>
      </Card>
    )

  if (isLoading && users.length === 0) {
    return wrap(
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, idx) => (
          <Skeleton key={idx} className="h-10 w-full rounded-lg" />
        ))}
      </div>,
      false
    )
  }

  if (!isLoading && (!data || users.length === 0)) {
    return wrap(
      <EmptyState
        description={t("adminDashboard.costsByUser.emptyDescription")}
        icon={Users}
        title={t("adminDashboard.costsByUser.emptyTitle")}
      />,
      false
    )
  }

  return wrap(
    <div className="space-y-6">
      <div className="bg-background rounded-xl border p-4">
        <div className="mb-4 flex flex-col gap-1">
          <div className="text-sm font-medium">{t("adminDashboard.costsByUser.trend.title")}</div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.costsByUser.trend.subtitle")}
          </div>
        </div>
        <CostUsageTrendChart currency={currency} data={trendData} isLoading={isTrendLoading} />
      </div>
      {data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {summaryCards.map((card) => {
            const Icon = card.icon
            return (
              <div
                key={card.id}
                className="bg-muted/30 flex items-center gap-3 rounded-xl border p-4"
              >
                <div className="bg-background text-muted-foreground rounded-lg p-2">
                  <Icon className="size-4" />
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">{card.label}</div>
                  <div className="text-lg font-semibold">{card.value}</div>
                </div>
              </div>
            )
          })}
        </div>
      )}
      <div className="space-y-2 lg:hidden">
        <MobileSortControls
          ariaLabel="Sort costs by user"
          direction={sort.direction}
          sortKey={sort.key}
          options={[
            { id: "user", label: t("adminDashboard.costsByUser.table.headers.user") },
            { id: "requests", label: t("adminDashboard.costsByUser.table.headers.requests") },
            { id: "tokens", label: t("adminDashboard.costsByUser.table.headers.tokens") },
            {
              id: "cost",
              label: t("adminDashboard.costsByUser.table.headers.cost", { currency }),
            },
            { id: "share", label: t("adminDashboard.costsByUser.table.headers.share") },
            {
              id: "activity",
              label: t("adminDashboard.costsByUser.table.headers.lastActivity"),
            },
          ]}
          onSortChange={(key, direction) =>
            setSort({ key: key as Exclude<SortState["key"], null>, direction })
          }
        />
        {visibleUsers.map((user) => (
          <div
            key={`${user.userId}-mobile`}
            className="bg-card space-y-3 rounded-lg border p-3"
            data-testid="cost-user-mobile-row"
          >
            <div className="truncate font-medium" title={getDisplayName(user)}>
              {getDisplayName(user)}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
              <div>
                <span className="text-muted-foreground block">Requests</span>
                <span className="font-semibold tabular-nums">
                  {formatCompactNumber(user.usage.requests)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block">Tokens</span>
                <span className="font-semibold tabular-nums">
                  {formatCompactNumber(user.usage.tokensTotal)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block">Cost</span>
                <span className="font-semibold tabular-nums">
                  {formatter.format(user.usage.cost)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block">Share</span>
                <span className="font-semibold tabular-nums">
                  {totalCost > 0 ? `${Math.round((user.usage.cost / totalCost) * 100)}%` : "—"}
                </span>
              </div>
              {typeof sharedPerUserMonthly === "number" ? (
                <>
                  <div>
                    <span className="text-muted-foreground block">
                      {t("localAiLeadPage.fullCost.shared", "Shared / month")}
                    </span>
                    <span className="font-semibold tabular-nums">
                      {formatter.format(sharedPerUserMonthly)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block">
                      {t("localAiLeadPage.fullCost.full", "Full cost / month")}
                    </span>
                    <span className="font-semibold tabular-nums">
                      {formatter.format(fullMonthlyCost(user.usage.cost))}
                    </span>
                  </div>
                </>
              ) : null}
            </div>
            <p className="text-muted-foreground text-xs">
              Last activity: {formatActivityDate(user.usage.lastActivity)}
            </p>
          </div>
        ))}
      </div>
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHeader
                align="left"
                className="min-w-[220px]"
                currentSort={sort}
                sortKey="user"
                onSort={handleSort}
              >
                {t("adminDashboard.costsByUser.table.headers.user")}
              </SortableTableHeader>
              <SortableTableHeader
                align="right"
                className="min-w-[120px]"
                currentSort={sort}
                sortKey="requests"
                onSort={handleSort}
              >
                {t("adminDashboard.costsByUser.table.headers.requests")}
              </SortableTableHeader>
              <SortableTableHeader
                align="right"
                className="min-w-[120px]"
                currentSort={sort}
                sortKey="tokens"
                onSort={handleSort}
              >
                {t("adminDashboard.costsByUser.table.headers.tokens")}
              </SortableTableHeader>
              <SortableTableHeader
                align="right"
                className="min-w-[140px]"
                currentSort={sort}
                sortKey="cost"
                onSort={handleSort}
              >
                {t("adminDashboard.costsByUser.table.headers.cost", { currency })}
              </SortableTableHeader>
              {showFullCost ? (
                <>
                  <TableHead className="min-w-[120px] text-right">
                    {t("localAiLeadPage.fullCost.shared", "Shared / month")}
                  </TableHead>
                  <SortableTableHeader
                    align="right"
                    className="min-w-[140px]"
                    currentSort={sort}
                    sortKey="full"
                    onSort={handleSort}
                  >
                    {t("localAiLeadPage.fullCost.full", "Full cost / month")}
                  </SortableTableHeader>
                </>
              ) : null}
              <SortableTableHeader
                align="right"
                className="min-w-[120px]"
                currentSort={sort}
                sortKey="share"
                onSort={handleSort}
              >
                {t("adminDashboard.costsByUser.table.headers.share")}
              </SortableTableHeader>
              <SortableTableHeader
                align="right"
                className="min-w-[120px]"
                currentSort={sort}
                sortKey="activity"
                onSort={handleSort}
              >
                {t("adminDashboard.costsByUser.table.headers.lastActivity")}
              </SortableTableHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleUsers.map((user) => (
              <TableRow key={user.userId}>
                <TableCell className="max-w-[260px] min-w-[220px]">
                  <div className="truncate font-medium" title={getDisplayName(user)}>
                    {getDisplayName(user)}
                  </div>
                </TableCell>
                <TableCell className="min-w-[120px] text-right tabular-nums">
                  {formatCompactNumber(user.usage.requests)}
                </TableCell>
                <TableCell className="min-w-[120px] text-right tabular-nums">
                  {formatCompactNumber(user.usage.tokensTotal)}
                </TableCell>
                <TableCell className="min-w-[140px] text-right tabular-nums">
                  <div className="flex flex-col items-end gap-1">
                    <span className="font-medium">{formatter.format(user.usage.cost)}</span>
                    <div className="bg-muted h-1.5 w-full max-w-[140px] rounded-full">
                      <div
                        className="bg-primary-ink/80 h-1.5 rounded-full"
                        style={{
                          width:
                            maxCost > 0
                              ? `${Math.max((user.usage.cost / maxCost) * 100, 0)}%`
                              : "0%",
                        }}
                      />
                    </div>
                  </div>
                </TableCell>
                {showFullCost ? (
                  <>
                    <TableCell className="text-muted-foreground min-w-[120px] text-right tabular-nums">
                      {formatter.format(sharedPerUserMonthly)}
                    </TableCell>
                    <TableCell className="min-w-[140px] text-right font-medium tabular-nums">
                      {formatter.format(fullMonthlyCost(user.usage.cost))}
                    </TableCell>
                  </>
                ) : null}
                <TableCell className="text-muted-foreground min-w-[80px] text-right tabular-nums">
                  {totalCost > 0 ? `${Math.round((user.usage.cost / totalCost) * 100)}%` : "—"}
                </TableCell>
                <TableCell className="text-muted-foreground min-w-[120px] text-right tabular-nums">
                  {formatActivityDate(user.usage.lastActivity)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {hasMore && (
        <Button
          className="w-full"
          type="button"
          variant="outline"
          onClick={() => setVisibleCount(sortedUsers.length)}
        >
          {t("adminDashboard.costsByUser.table.showMore", {
            count: sortedUsers.length - visibleCount,
          })}
        </Button>
      )}
      {isExpanded && (
        <Button
          className="w-full"
          type="button"
          variant="outline"
          onClick={() => setVisibleCount(INITIAL_VISIBLE_COUNT)}
        >
          {t("adminDashboard.costsByUser.table.showLess")}
        </Button>
      )}
    </div>,
    true
  )
})
